import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GlobeIcon, ChevronDownIcon, CheckIcon,
  WifiIcon,
  WifiOffIcon,
  BatteryIcon,
  BatteryChargingIcon,
  BatteryFullIcon,
  BatteryMediumIcon,
  BatteryLowIcon,
  CloudIcon,
  CloudRainIcon,
  CloudSnowIcon,
  CloudLightningIcon,
  CloudFogIcon,
  SunIcon,
  BellIcon,
  Maximize2Icon,
  Minimize2Icon,
  SettingsIcon,
  LogOutIcon,
  Building2Icon,
  WindIcon,
  CheckSquareIcon,
  ArrowLeftRightIcon } from
'lucide-react';
interface SystemStatusBarProps {
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  onOpenSettings?: () => void;
  isSettingsOpen?: boolean;
  onOpenIssueBoard?: () => void;
  isIssueBoardOpen?: boolean;
  apartmentLabel?: string;
  onChangeSpace?: () => void;
  hasAlarms?: boolean;
  apartmentAssetId?: string | null;
}

import { useTranslation } from 'react-i18next';
import { useWeather } from '../hooks/useWeather';
import { useDeviceState } from '../hooks/useDeviceState';

export function SystemStatusBar({
  isFullscreen = false,
  onToggleFullscreen,
  onOpenSettings,
  isSettingsOpen = false,
  onOpenIssueBoard,
  isIssueBoardOpen = false,
  apartmentLabel,
  onChangeSpace,
  hasAlarms = false,
  apartmentAssetId = null
}: SystemStatusBarProps) {
  const { t, i18n } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const { site_temperature, weatherCode, pm25 } = useWeather(apartmentAssetId);
  const { isOnline, batteryLevel, isCharging } = useDeviceState();
  
  const getWeatherInfo = (code: number | null) => {
    if (code === null) return { Icon: CloudIcon, color: 'text-slate-400' };
    // WMO Weather interpretation codes (WW)
    // 0: Clear sky
    // 1, 2, 3: Mainly clear, partly cloudy, and overcast
    // 45, 48: Fog and depositing rime fog
    // 51, 53, 55: Drizzle: Light, moderate, and dense intensity
    // 56, 57: Freezing Drizzle: Light and dense intensity
    // 61, 63, 65: Rain: Slight, moderate and heavy intensity
    // 66, 67: Freezing Rain: Light and heavy intensity
    // 71, 73, 75: Snow fall: Slight, moderate, and heavy intensity
    // 77: Snow grains
    // 80, 81, 82: Rain showers: Slight, moderate, and violent
    // 85, 86: Snow showers slight and heavy
    // 95: Thunderstorm: Slight or moderate
    // 96, 99: Thunderstorm with slight and heavy hail

    if (code === 0 || code === 1) return { Icon: SunIcon, color: 'text-amber-400' };
    if (code === 2 || code === 3) return { Icon: CloudIcon, color: 'text-slate-200' };
    if (code === 45 || code === 48) return { Icon: CloudFogIcon, color: 'text-slate-400' };
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return { Icon: CloudRainIcon, color: 'text-cyan-400' };
    if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) return { Icon: CloudSnowIcon, color: 'text-sky-200' };
    if (code >= 95 && code <= 99) return { Icon: CloudLightningIcon, color: 'text-yellow-400' };
    
    return { Icon: CloudIcon, color: 'text-slate-400' };
  };
  
  const { Icon: WeatherIcon, color: weatherColor } = getWeatherInfo(weatherCode);
  const currentDate = new Date().toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  const currentTime = new Date().toLocaleTimeString(i18n.language === 'ko' ? 'ko-KR' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
  return (
    <div className="w-full flex items-center py-4 px-6 border-b border-white/5 bg-[#0a0f1a]/50 backdrop-blur-md sticky top-0 z-20">
      {/* Left Section */}
      <div className="flex items-center space-x-6 whitespace-nowrap flex-shrink-0">
        <div className="flex items-center space-x-2 text-slate-400">
          <WeatherIcon className={`w-7 h-7 flex-shrink-0 drop-shadow-md ${weatherColor}`} />
          <span className="text-sm font-medium font-bold">
            {t('system.out_temp')}: {site_temperature !== null ? `${site_temperature.toFixed(1)}°C` : '...'}
          </span>
        </div>
        <div className="h-4 w-px bg-white/10" />
        <div className="flex items-center space-x-2 text-slate-400">
          <WindIcon className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium font-bold">
            {t('system.pm25')}: <span className={
              pm25 !== null
                ? pm25 <= 15 ? 'text-emerald-400'
                : pm25 <= 35 ? 'text-cyan-400'
                : pm25 <= 75 ? 'text-amber-400'
                : 'text-red-400'
                : 'text-slate-400'
            }>
              {pm25 !== null
                ? pm25 <= 15 ? t('system.pm25_good')
                : pm25 <= 35 ? t('system.pm25_fair')
                : pm25 <= 75 ? t('system.pm25_bad')
                : t('system.pm25_very_bad')
                : '...'}
            </span>
          </span>
          <span className="text-sm font-roboto text-slate-500">
            {pm25 !== null ? `${Math.round(pm25)}µg/m³` : ''}
          </span>
        </div>
      </div>

      {/* Center Section: UNIT INFO */}
      <div className="flex-1 flex justify-center">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 px-6 py-2 rounded-full bg-white/5 border border-white/10"
        >
          <Building2Icon className="w-4 h-4 text-cyan-400" />
          <div className="flex flex-col items-center">
            <span className="text-[1.0rem] font-bold font-roboto text-slate-200 tracking-tight leading-none uppercase">
              {apartmentLabel || 'No Unit Selected'}
            </span>
          </div>
        </motion.div>
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-6 whitespace-nowrap flex-shrink-0">
      
          <div className="text-xs text-slate-500 font-roboto uppercase tracking-wider">
            {currentDate}
          </div>
          <div className="text-sm text-slate-200 font-roboto font-bold">
            {currentTime}
          </div>

        <div className="flex items-center space-x-4 pl-6 border-l border-white/10">
          <button 
            className="p-2 rounded-full hover:bg-white/5 transition-colors relative group"
            title={t('system.notifications')}
          >
            <BellIcon className="w-4 h-4 text-slate-400 group-hover:text-cyan-400 transition-colors" />
            {hasAlarms && (
              <span className="absolute top-1.5 right-2 w-1.5 h-1.5 bg-red-500 rounded-full shadow-[0_0_5px_rgba(239,68,68,0.8)]" />
            )}
          </button>
          {onToggleFullscreen &&
          <button
            onClick={onToggleFullscreen}
            className="p-2 rounded-full hover:bg-white/5 transition-colors group"
            title={isFullscreen ? t('system.exit_fullscreen') : t('system.fullscreen')}>

              {isFullscreen ?
            <Minimize2Icon className="w-4 h-4 text-cyan-400 group-hover:text-cyan-300 transition-colors" /> :

            <Maximize2Icon className="w-4 h-4 text-slate-400 group-hover:text-cyan-400 transition-colors" />
            }
            </button>
          }
          <div className={`flex items-center space-x-1 ${isOnline ? 'text-emerald-400' : 'text-red-400'}`}>
            {isOnline ? <WifiIcon className="w-4 h-4" /> : <WifiOffIcon className="w-4 h-4" />}
          </div>
          <div className={`flex items-center space-x-1 ${batteryLevel !== null && batteryLevel <= 20 && !isCharging ? 'text-red-400' : 'text-slate-400'}`}>
            {isCharging ? (
              <BatteryChargingIcon className="w-4 h-4 text-emerald-400" />
            ) : batteryLevel === null ? (
              <BatteryIcon className="w-4 h-4" />
            ) : batteryLevel > 80 ? (
              <BatteryFullIcon className="w-4 h-4" />
            ) : batteryLevel > 30 ? (
              <BatteryMediumIcon className="w-4 h-4" />
            ) : (
              <BatteryLowIcon className="w-4 h-4" />
            )}
            <span className="text-xs font-roboto">
              {batteryLevel !== null ? `${batteryLevel}%` : '--%'}
            </span>
          </div>
          <button 
            onClick={onOpenSettings}
            className={`p-2 rounded-full transition-colors group ${isSettingsOpen ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-white/5 text-slate-400'}`}
            title={t('system.settings')}
          >
            <SettingsIcon className={`w-4 h-4 ${isSettingsOpen ? 'text-cyan-400' : 'group-hover:text-cyan-400 transition-colors'}`} />
          </button>

          {onOpenIssueBoard && (
            <button
              onClick={onOpenIssueBoard}
              className={`p-2 rounded-full transition-colors group relative ${isIssueBoardOpen ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-white/5 text-slate-400'}`}
              title="이슈 트래킹 보드"
            >
              <CheckSquareIcon className={`w-4 h-4 ${isIssueBoardOpen ? 'text-cyan-400' : 'group-hover:text-cyan-400 transition-colors'}`} />
            </button>
          )}

          {onChangeSpace && (
            <button 
              onClick={onChangeSpace}
              className="p-2 rounded-full hover:bg-cyan-500/10 text-slate-500 hover:text-cyan-400 transition-all group"
              title="공간 이동"
            >
              <ArrowLeftRightIcon className="w-4 h-4" />
            </button>
          )}
          
          {/* Language Selector */}
          <div className="relative">
            <button 
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="flex items-center justify-center gap-1 w-14 py-1.5 rounded-lg hover:bg-white/5 transition-colors text-slate-400 hover:text-cyan-400 group"
              title="Change Language"
            >
              <GlobeIcon className="w-4 h-4 mr-0.5" />
              <span className="text-xs font-bold uppercase">{i18n.language}</span>
              <ChevronDownIcon className={`w-3 h-3 transition-transform ${isLangMenuOpen ? 'rotate-180': ''}`} />
            </button>
            
            <AnimatePresence>
              {isLangMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-48 bg-[#1e293b] rounded-xl border border-white/10 shadow-xl overflow-hidden z-[60] py-1"
                >
                  <div className="px-3 py-2 text-xs font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 mb-1">
                    Select Language
                  </div>
                  <button
                    onClick={() => { i18n.changeLanguage('ko'); setIsLangMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-white/5 flex justify-between items-center transition-colors"
                  >
                    한국어 (KO)
                    {i18n.language === 'ko' && <CheckIcon className="w-4 h-4 text-cyan-400" />}
                  </button>
                  <button
                    onClick={() => { i18n.changeLanguage('en'); setIsLangMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-white/5 flex justify-between items-center transition-colors"
                  >
                    English (EN)
                    {i18n.language === 'en' && <CheckIcon className="w-4 h-4 text-cyan-400" />}
                  </button>
                  <button
                    onClick={() => { i18n.changeLanguage('th'); setIsLangMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-white/5 flex justify-between items-center transition-colors"
                  >
                    ภาษาไทย (TH)
                    {i18n.language === 'th' && <CheckIcon className="w-4 h-4 text-cyan-400" />}
                  </button>
                  <button
                    onClick={() => { i18n.changeLanguage('ja'); setIsLangMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-white/5 flex justify-between items-center transition-colors"
                  >
                    日本語 (JA)
                    {i18n.language === 'ja' && <CheckIcon className="w-4 h-4 text-cyan-400" />}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
        </div>
      </div>
    </div>);

}